import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {MenuItem} from './MenuItem'
import {MenuInnerWithSub} from './MenuInnerWithSub'
import {useAuth} from '../../../../../app/modules/auth'

export function SystemMenu(): JSX.Element {
  const {t} = useTranslation()
  const {hasPermission} = useAuth()
  const base = '/system-mgt/'

  const menuItems = useMemo(
    () => [
      {permission: null, title: t('MENU.HOME'), to: `${base}home`},

      // submenu
      {
        permission: 'user-list',
        title: t('MENU.USERS'),
        to: '',
        children: [
          {
            permission: 'user-list',
            title: t('MENU.USERS'),
            to: `${base}users`,
            fontIcon: 'fas fa-users',
          },
          {
            permission: 'role-list',
            title: t('MENU.ROLES'),
            to: `${base}roles`,
            fontIcon: 'fas fa-user-shield',
          },
        ],
      },

      {
        permission: 'campus-list',
        title: t('MENU.CAMPUSES'),
        to: '',
        children: [
          {
            permission: 'campus-list',
            title: t('MENU.CAMPUSES'),
            to: `${base}campuses`,
            fontIcon: 'fas fa-university',
          },
          {
            permission: 'campus-unit-list',
            title: t('MENU.CAMPUS_UNITS'),
            to: `${base}campus-units`,
            fontIcon: 'fas fa-landmark',
          },
        ],
      },

      {
        permission: 'faculty-list',
        title: t('MENU.FACULTIES'),
        to: '',
        children: [
          {
            permission: 'faculty-list',
            title: t('MENU.FACULTIES'),
            to: `${base}faculties`,
            fontIcon: 'fas fa-chalkboard-teacher',
          },
          {
            permission: 'campus-unit-faculty-list',
            title: t('MENU.CAMPUS_UNIT_FACULTIES'),
            to: `${base}cu-faculties`,
            fontIcon: 'fas fa-building',
          },
        ],
      },

      {permission: 'grading-score-list', title: t('LABEL.GRADING_SYSTEM'), to: `${base}grading`},

      {
        permission: null,
        title: t('MENU.SCHOOL'),
        to: '',
        children: [
          {
            permission: 'school-subject-list',
            title: t('MENU.SUBJECTS'),
            to: `${base}subjects`,
            fontIcon: 'fas fa-book',
          },
          {
            permission: 'school-subject-list',
            title: t('MENU.ASSIGN_CLASS_SUBJECTS'),
            to: `${base}class-subjects`,
            fontIcon: 'fas fa-layer-group',
          },
          {
            permission: 'campus-class-list',
            title: t('MENU.CLASSES'),
            to: `${base}classes`,
            fontIcon: 'fas fa-chalkboard',
          },
          {
            permission: 'school-book-list',
            title: t('MENU.BOOKS'),
            to: `${base}books`,
            fontIcon: 'fas fa-book-open',
          },
          {
            permission: 'school-book-list',
            title: t('MENU.ASSIGN_CLASS_BOOKS'),
            to: `${base}class-books`,
            fontIcon: 'fas fa-book-open',
          },
          {
            permission: 'school-uniform-list',
            title: t('MENU.UNIFORMS'),
            to: `${base}uniforms`,
            fontIcon: 'fas fa-tshirt',
          },
          {
            permission: 'school-uniform-list',
            title: t('MENU.ASSIGN_CLASS_UNIFORMS'),
            to: `${base}class-uniforms`,
            fontIcon: 'fas fa-tshirt',
          },
          {
            permission: 'school-transport-list',
            title: t('MENU.TRANSPORTS'),
            to: `${base}transports`,
            fontIcon: 'fas fa-bus',
          },
        ],
      },
      {
        permission: null,
        title: t('MENU.TOOLS'),
        to: '',
        children: [
          {
            permission: 'relative-list',
            title: t('MENU.RELATIVES'),
            to: `${base}relatives`,
            fontIcon: 'fas fa-user-friends',
          },
        ],
      },
      {
        permission: null,
        title: t('MENU.FINANCE'),
        to: '',
        children: [
          {
            permission: 'finance-categories-list',
            title: t('MENU.FINANCE_CATEGORIES'),
            to: `${base}finance-categories`,
            fontIcon: 'fas fa-wallet',
          },
          {
            permission: 'finance-categories-list',
            title: t('MENU.FINANCE_SUB_CATEGORIES'),
            to: `${base}finance-sub-categories`,
            fontIcon: 'fas fa-list-ul',
          },
        ],
      },
    ],
    [t]
  )

  const renderEntry = (item: any, index: any) => {
    if (item.permission && !hasPermission(item.permission)) return null

    if (item.children) {
      const visibleChildren = item.children.filter(
        (c: any) => !c.permission || hasPermission(c.permission)
      )
      if (!visibleChildren.length) return null

      return (
        <MenuInnerWithSub
          key={index}
          title={item.title}
          to={item.to}
          menuPlacement='bottom-start'
          menuTrigger='click'
          hasArrow={false}
        >
          {visibleChildren.map((child: any, index: any) => (
            <MenuItem key={index} to={child.to} title={child.title} fontIcon={child.fontIcon} />
          ))}
        </MenuInnerWithSub>
      )
    }

    return <MenuItem key={item.to} title={item.title} to={item.to} />
  }

  return <>{menuItems.map(renderEntry)}</>
}
